<template>
    <div v-if="products.length">
        <div class="flex items-center space-x-4">
            <slot name="pre-picker"></slot>
            <div class="space-y-1 text-sm text-gray-800">
                <u-variant-picker :parent="parent" :products="productMap" :sample="sample" :show-header="showHeader" :is-approximate-measurement="isApproximateMeasurement">
                    <template #product-info="slotProps">
                        <slot name="product-info" v-bind="slotProps" />
                    </template>
                </u-variant-picker>
            </div>
        </div>
        <div class="mt-4 flex space-x-3">
            <a @click="submit" role="button"
                class="flex-1 bg-orange-500 text-white font-semibold py-2 rounded-md hover:bg-orange-600 text-center pointer">
                {{ submitButtonText }}
            </a>
        </div>
    </div>
</template>

<script>

import Swal from 'sweetalert2/dist/sweetalert2.js';
export default {

    props: {
        parent: {
            type: Object,
            required: true,
        },
        products: {
            type: Array,
            required: true,
        },
        sample: {
            type: String,
            required: false,
            default: "false",
        },
        isApproximateMeasurement: {
            type: Boolean,
            required: true,
        },
        submitButtonText: {
            type: String,
            required: false,
            default: "Add to Basket"
        },
        showHeader: {
            type: Boolean,
            required: false,
            default: true
        },
        itemKey: {
            type: String,
            required: false,
        }
    },
    methods: {
        submit() {
            const baseURL = '/api/basket';
            const URL = this.itemKey ? `${baseURL}/${this.itemKey}` : baseURL;

            axios.post(URL, {
                _token: this.csrfToken,
                _method: 'PUT',
                type: 'brick',
                parent: this.parent.sku,
                products: Object.fromEntries(Object.entries(this.productMap)
                ),
            }).then(({ data }) => {
                if (this.itemKey) {
                    // Refresh the window after updating the item quantity
                    window.location.reload();
                } else {
                    Orbit.$emit('global::basket:update', data);
                }

                const bricks = this.processBricks(data);

                const brickHtml = bricks.map(brick => `
                    <div class="mb-4">
                        <span class="text-gray-600">${brick.quantity} ×</span>
                        <span class="font-semibold">${brick.name}</span>
                        <br>
                        <span class="text-gray-600 text-sm">${brick.size}</span>
                        <br>
                        <span class="text-gray-400 text-sm">${brick.type ?? ''}</span>
                    </div>
                `).join('');

                Swal.fire({
                    title: '<span class="text-green-600 font-semibold text-xlg">Bricks added to your basket</span>',
                    html: brickHtml,
                    showCancelButton: true,
                    confirmButtonText: 'Proceed to checkout',
                    cancelButtonText: 'Continue shopping',
                    buttonsStyling: false,
                    customClass: {
                        actions: 'swal-buttons flex flex-col-reverse sm:flex-row justify-end gap-4 mt-2 mb-0 sm:mb-3',
                        cancelButton: 'order-1 px-4 py-2 text-black font-semibold border border-orange-500 text-orange-500 rounded-md bg-white hover:bg-orange-100',
                        confirmButton: 'order-2 px-4 py-2 text-white font-semibold rounded-md bg-orange-500 hover:bg-orange-600',
                    },
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.location.href = '/basket';
                    }
                });

            }).catch(({ response }) => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error adding item',
                    text: response.data.error ?? 'An unexpected error occurred.',
                    timerProgressBar: true,
                    timer: 6000,
                });
            });
        },

        // Returns e.g.
        // [
        //     { quantity: 80, name: "Britannia Red", size: "H 60mm × L 215mm × D 102mm", type: "Engineering Brick" },
        //     { quantity: 400, name: "Camtech Fuji", size: "H 65mm × L 215mm × D 102mm", type: "Standard" },
        // ]
        processBricks(data) {
            const output = [];

            data.items.items.forEach(item => {
                const brick = item.brick;
                const products = item.products;

                Object.values(products).forEach(product => {
                    const measurements = product.measurements;
                    let size = 'H 65mm × L 215mm × D 102.5mm';

                    if (measurements &&
                        measurements.height && measurements.height !== '0' &&
                        measurements.length && measurements.length !== '0' &&
                        measurements.depth && measurements.depth !== '0')
                    {
                        size = [
                            `H ${measurements.height}mm`,
                            `L ${measurements.length}mm`,
                            `D ${measurements.depth}mm`
                        ].join(' × ');
                    }

                    const existingBrick = output.find(b =>
                        b.name === brick.name &&
                        b.size === size &&
                        b.type === product.type
                    );

                    if (existingBrick) {
                        existingBrick.quantity += product.quantity;
                    } else {
                        output.push({
                            quantity: product.quantity,
                            name: brick.name,
                            size: size,
                            type: product.type
                        });
                    }
                });
            });
            return output;
        }
    },
    data() {
        return {
            productMap: this.products.reduce((acc, product) => {
                acc[product.id] = {
                    ...product,
                    quantity: product.quantity ?? 0
                };
                return acc;
            }, {})
        };
    }
};
</script>
